import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, describeApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RotateCcw, Lock, CheckCircle2, AlertTriangle } from "lucide-react";

const fmtElapsed = (s) => {
  if (s == null) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
};
const fmtRemaining = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m} min ${String(sec).padStart(2, "0")} s`;
};

// P6K/P6M — recovery Owner GET-only del segmento differito (S0 di HARD2,
// S6 di HARD3, …): nessuna nuova inferenza, nessuna nuova POST. Un tap =
// una sola richiesta (lock sincrono useRef + disabilitazione DOM immediata).
export const HardenedRecoveryPanel = ({ jobId }) => {
  const [r, setR] = useState(null);
  const [checked, setChecked] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [err, setErr] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const lock = useRef(false);
  const btnRef = useRef(null);
  const cbRef = useRef(null);
  const inpRef = useRef(null);

  const load = useCallback(() => {
    if (!jobId) return;
    api.get(`/reader/jobs/${jobId}/segmented-run-recovery/status`)
      .then(({ data }) => setR(data))
      .catch((e) => setR({ recovery_applicable: false, load_error: describeApiError(e) }));
  }, [jobId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (!r) return null;
  if (r.load_error) {
    return (
      <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900" data-testid="recovery-load-error">
        Stato recovery non disponibile: {r.load_error.message}
      </div>
    );
  }
  const seg = r.segment || {};
  const sid = seg.segment_id || r.profile?.segment_id || "";
  const ra = r.recovery_authorization;
  const inProgress = r.recovery_state === "RECOVERY_IN_PROGRESS_GET_ONLY";
  const done = (r.recovery_state || "").startsWith("SEGMENT_COMPLETED");
  const terminal = r.recovery_state === "SEGMENT_PROVIDER_TERMINAL_FAILURE_NO_REPOST";
  const superseded = !!r.superseded_by_owner_decision;
  if (!r.recovery_applicable && !inProgress && !done && !terminal && !(ra && ra.consumed)) return null;

  const PHRASE = ra?.confirmation_phrase || "";
  const phraseOk = PHRASE.length > 0 && phrase === PHRASE;
  const canStart = r.startable && checked && phraseOk && !submitting && !accepted && !attempted;
  const locked = submitting || !!accepted || attempted || !r.startable;
  const maxGets = r.limits?.max_new_gets;
  const maxMin = Math.round((r.limits?.max_new_seconds || 0) / 60);
  const recGets = seg.recovery_gets_performed ?? (ra?.gets_performed ?? 0);
  const newPosts = seg.new_posts_since_recovery_auth ?? 0;
  const remaining = inProgress ? seg.recovery_remaining_s : (ra?.consumed ? 0 : (r.limits?.max_new_seconds ?? null));

  const onResume = async (ev) => {
    ev?.preventDefault?.();
    if (lock.current) return;
    lock.current = true;
    [btnRef.current, cbRef.current, inpRef.current].forEach((el) => { if (el) el.disabled = true; });
    setSubmitting(true); setAttempted(true); setErr(null);
    try {
      const res = await api.post(`/reader/jobs/${jobId}/segmented-run-recovery/resume-get-only`,
        { confirm: true, confirmation_text: phrase });
      if (res.status === 202 || res.status === 200) { setAccepted(res.data || { accepted: true }); load(); }
      else setErr({ status: res.status, code: null, message: `Risposta inattesa HTTP ${res.status}.` });
    } catch (e) {
      setErr(describeApiError(e)); load();
    } finally { setSubmitting(false); }
  };

  return (
    <div className="w-full min-w-0 border-2 border-sky-400 bg-sky-50/40 p-2 sm:p-3 space-y-2 break-anywhere"
         data-testid="recovery-panel">
      <div className="font-bold text-sm flex items-center gap-2" data-testid="recovery-title">
        <RotateCcw size={15} className="text-amber-700 shrink-0" />
        {r.panel_title || `Recovery ${sid} — solo GET`} (nessuna nuova inferenza)
      </div>
      <div className="text-[11px]" data-testid="recovery-explanation">{r.explanation}</div>
      <div className="text-[11px] px-2 py-1 border border-amber-300 bg-white" data-testid="recovery-state">
        Stato recovery: <b>{r.recovery_state}</b>
        {done && <span> — {sid} completato{r.recovery_state.endsWith("HUMAN_REVIEW_REQUIRED") ? ": tutti i segmenti completi, fusione eseguita, esito HUMAN_REVIEW_REQUIRED." : ": il run prosegue con i segmenti rimanenti con l'autorizzazione già concessa."}</span>}
        {terminal && <span> — il provider ha chiuso la risposta con esito <b>{ra?.provider_final_status || seg.state}</b>: nessun reinvio, decisione Owner.</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px]" data-testid="recovery-facts">
        <div>response_id: <span className="font-mono" data-testid="recovery-response-id">{seg.response_id_short || "—"}</span>{seg.response_id_present && <span className="text-emerald-800"> (persistita)</span>}</div>
        <div>POST {sid} = <b data-testid="recovery-post-count">{seg.post_count ?? "—"}</b> · GET già effettuati: <b data-testid="recovery-get-count">{seg.get_count ?? "—"}</b></div>
        <div>GET recovery effettuati / massimi: <b data-testid="recovery-gets-progress">{recGets} / {maxGets}</b></div>
        <div>Tempo residuo recovery: <b data-testid="recovery-remaining">{inProgress ? fmtRemaining(remaining) : (ra?.consumed ? "concluso" : `${maxMin} min (dall'avvio)`)}</b></div>
        <div>Stato provider: <b data-testid="recovery-provider-status">{seg.last_provider_status || "—"}</b></div>
        <div>Durata trascorsa dalla POST: <b data-testid="recovery-elapsed">{fmtElapsed(seg.elapsed_since_post_s)}</b></div>
        <div>Nuove POST: <b data-testid="recovery-new-posts">{newPosts}</b> (massimo {r.limits?.max_new_posts ?? 0} — mai ripetuta)</div>
        <div>Backoff GET: <b>{(r.limits?.backoff_s || []).join(" / ")} s</b></div>
        <div>Costo confermato: <b data-testid="recovery-cost">${r.budget?.confirmed_usd ?? 0}</b> · esposizione max {sid}: ${r.budget?.segment_worst_usd ?? "—"}</div>
        <div>Budget residuo: <b data-testid="recovery-residual">${r.budget?.residual_usd ?? "—"}</b> / ${r.budget?.cap_usd}</div>
        <div className="sm:col-span-2 text-muted-foreground">Totali dai record segmento: POST {seg.totals_from_segments?.post_total} · GET {seg.totals_from_segments?.get_total}</div>
      </div>
      {seg.errors?.length > 0 && (
        <div className="text-[10px] border border-red-300 bg-red-50 p-1.5 text-red-900 break-anywhere" data-testid="recovery-provider-error">
          Errore provider reale: {seg.errors.map((e, i) => <span key={i}>{[e.phase, e.envelope_status, e.exception_type, e.detail].filter(Boolean).join(" · ")}{i < seg.errors.length - 1 ? "; " : ""}</span>)}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground" data-testid="recovery-continuation-note">{r.continuation_note}</div>

      {ra && (
        <div className="text-[10px] text-muted-foreground" data-testid="recovery-auth">
          Autorizzazione recovery <span className="font-mono">{ra.id}</span> · segmento {ra.segment_id} · {ra.consumed ? `consumata ${(ra.consumed_at || "").slice(0, 16)}` : "non consumata"} · scade {(ra.expires_at || "").slice(0, 16)} · max {ra.max_gets} GET / {Math.round((ra.max_seconds || 0) / 60)} min · {ra.max_posts} POST
          {ra.outcome && <span> · esito: <b>{ra.outcome}</b> ({ra.gets_performed} GET, {ra.posts_performed ?? 0} POST)</span>}
        </div>
      )}
      {!ra && r.recovery_applicable && !superseded && (
        <div className="text-[11px] border border-amber-300 bg-white p-2" data-testid="recovery-no-auth">
          Autorizzazione di recovery non ancora predisposta: serve una decisione Owner separata (solo GET).
        </div>
      )}
      {superseded && (
        <div className="text-[11px] border border-slate-300 bg-slate-50 p-2 text-slate-800" data-testid="recovery-superseded">
          Decisione Owner: <b>{r.superseded_by_owner_decision.decision}</b>
          {r.superseded_by_owner_decision.superseded_by?.logical_reader_run_id
            ? <span> → run <span className="font-mono">{r.superseded_by_owner_decision.superseded_by.logical_reader_run_id}</span></span> : null}
          . Stato storico: nessun ulteriore GET, nessuna nuova autorizzazione.
        </div>
      )}
      {r.preconditions && !r.preconditions.ok && !inProgress && !superseded && !done && !terminal && (
        <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900" data-testid="recovery-precondition-failed">
          Precondizioni non soddisfatte: {r.preconditions.failing.join(", ")}
        </div>
      )}

      {accepted && (
        <div className="flex items-start gap-1.5 text-[11px] border border-emerald-400 bg-emerald-50 p-2 text-emerald-900" data-testid="recovery-accepted">
          <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
          <span>Ripresa accettata (HTTP 202): solo GET sulla response_id {accepted.response_id_short}, max {accepted.max_new_gets} GET, 0 POST. Autorizzazione di recovery consumata.</span>
        </div>
      )}
      {err && (
        <div className="text-[11px] border border-red-400 bg-red-50 p-2 text-red-900 space-y-0.5" data-testid="recovery-error">
          <div className="font-bold">Ripresa non eseguita{err.status ? ` — HTTP ${err.status}` : ""}{err.code ? ` · ${err.code}` : ""}</div>
          <div>{err.message}</div>
          <div className="text-[10px]">Nessun nuovo tentativo automatico.</div>
        </div>
      )}

      {r.startable && !accepted && !attempted && (
        <div className="border-t border-amber-300 pt-2 space-y-2 safe-pb">
          <label className="flex items-start gap-2 text-[11px]">
            <input type="checkbox" ref={cbRef} checked={checked} disabled={locked}
                   onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0"
                   data-testid="recovery-owner-checkbox" />
            <span>Confermo come Owner la ripresa del solo polling GET di {sid} sulla response_id salvata (nessuna nuova inferenza, nessuna nuova POST, max {maxGets} GET entro {maxMin} min).</span>
          </label>
          <input type="text" ref={inpRef} value={phrase} disabled={locked}
                 onChange={(e) => setPhrase(e.target.value)}
                 placeholder={`Digita esattamente: ${PHRASE}`}
                 className="w-full min-h-[44px] border border-border px-2 py-2 text-base font-mono bg-white"
                 data-testid="recovery-confirmation-input" />
          <Button ref={btnRef} onClick={onResume} disabled={!canStart}
                  data-testid="recovery-resume-button"
                  className="w-full min-h-[44px] bg-amber-700 hover:bg-amber-800">
            {canStart ? <RotateCcw size={14} className="mr-1" /> : <Lock size={14} className="mr-1" />}
            {submitting ? "Ripresa in corso…" : `Riprendi ${sid} solo GET (0 nuove POST)`}
          </Button>
        </div>
      )}
      {ra && ra.consumed && !accepted && !inProgress && !done && !terminal && !superseded && (
        <div className="flex items-start gap-1.5 text-[10px] text-amber-800" data-testid="recovery-consumed">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>Autorizzazione di recovery consumata (one-shot): stato storico.</span>
        </div>
      )}
    </div>
  );
};
